/* eslint-disable @typescript-eslint/ban-ts-comment,@typescript-eslint/no-explicit-any */

import { actions, Thunk } from "./module";
import {
  Customization,
  Model,
  PriceGroup,
  SelectedOption,
  SpecialOptions,
  UserAction,
} from "./types";

type RegionCustomization = {
  optionToAttributeId?: number;
  attributeName?: string;
  id?: number;
  attributeId: number;
  showDropdown: boolean;
  activeValues: number;
  values?: Lookup<Customization>;
};
type OptionRegion = {
  selectedOptions?: Lookup<number>;
  id: number;
  regionName: string;
  actualHeight: number;
  actualWidth: number;
  isMutuallyExclusive: boolean;
  active: boolean;
  customizations?: Lookup<RegionCustomization>;
};
export type NavigationKeyData = {
  id?: number;
  optionName?: string;
  type?: string;
  icon?: string;
  regions?: Lookup<OptionRegion>;
};
type RegionResult = {
  region: string;
  id: number;
  data?: Model;
};

const getNavigationKeyData: Thunk<NavigationKeyData[]> =
  (state, dispatch) => () => {
    const { navigationViewModelData } = state;

    // loop through the navigation view model data and return a navigation key object
    const navigationKeyData: NavigationKeyData[] = [];
    const options = navigationViewModelData?.customizableOptions;

    for (const option in options) {
      const currentNavigationKey: NavigationKeyData = {
        id: Number(option),
        optionName: options[option]?.optionName,
      };

      switch (options[option]?.optionName) {
        case "image":
          currentNavigationKey.type = "Image/Logo";
          currentNavigationKey.icon = "icon-picture";
          break;
        case "text":
          currentNavigationKey.type = "Text";
          currentNavigationKey.icon = "icon-fontsize";
          break;
        default:
      }

      // populate regions that belong to this option
      const currentOptionRegions: Lookup<OptionRegion> = {};
      const regions = options[option]?.regions;

      for (const region in regions) {
        currentOptionRegions[region] = {
          id: Number(region),
          regionName: regions[region]?.regionName ?? "",
          actualHeight: regions[region]?.actualHeight ?? 0,
          actualWidth: regions[region]?.actualWidth ?? 0,
          isMutuallyExclusive: regions[region]?.isMutuallyExclusive ?? false,
          active: true,
        };

        const currentRegionCustomizations: Lookup<RegionCustomization> = {};
        const customizations = options[option]?.regions[region]?.customizations;

        for (const customization in customizations) {
          const attributeId = Number(
            customizations[customization]?.attributeId,
          );

          if (!currentRegionCustomizations[attributeId]) {
            currentRegionCustomizations[attributeId] = {
              attributeId,
              showDropdown: false,
              activeValues: 0,
            };
          }
          const currentAttribute = currentRegionCustomizations[attributeId]!;

          currentAttribute.id = attributeId;
          currentAttribute.attributeName =
            customizations[customization]?.attributeName;
          currentAttribute.optionToAttributeId =
            customizations[customization]?.optionToAttributeId;

          if (!currentAttribute.values) {
            currentAttribute.values = {};
          }
          currentAttribute.values[customization] = {};
          const currentAttributeValue =
            currentAttribute.values[customization] || {};

          currentAttributeValue.id = Number(customization);
          currentAttributeValue.isGreaterOrEqualTo = Boolean(
            customizations[customization]?.isGreaterOrEqualTo,
          );
          currentAttributeValue.numericValue = Number(
            customizations[customization]?.numericValue,
          );
          currentAttributeValue.pricedAttributeValueId = Number(
            customizations[customization]?.pricedAttributeValueId,
          );
          currentAttributeValue.stringValue =
            customizations[customization]?.stringValue;
          currentAttributeValue.displayValue =
            customizations[customization]?.displayValue;

          // check if we have user actions
          // otherwise check if the current attribute is part of the price group the user's action list is in
          let currentAttributeIsActive = false;

          for (const priceId in navigationViewModelData.priceGroups) {
            let priceGroup: string[] = [];
            if (navigationViewModelData.priceGroups[priceId] !== undefined) {
              priceGroup = Object.keys(
                navigationViewModelData.priceGroups[priceId]!.attributesValues,
              );
            }

            // check if the current attribute is part of a price group
            // and if all the user actions are part of this price group

            // when looking at user actions, we want to consider the currentAttributeValue for pricing when the user actions
            // don't have the currentAttribute in it

            // make a copy of user actions without the current attribute

            const userActionsWithoutCurrentAttribute: Lookup<any> = {
              ...getUserActions(state, dispatch)(),
            };

            Object.entries(userActionsWithoutCurrentAttribute).forEach(
              ([action, userAction]) => {
                if (
                  currentAttribute.attributeName !== "default" &&
                  userAction?.attributeId === currentAttribute.id &&
                  userAction?.optionToAttributeId ===
                    currentAttribute.optionToAttributeId
                ) {
                  delete userActionsWithoutCurrentAttribute[action];
                }
              },
            );

            if (
              priceGroup.includes(customization) &&
              Object.keys(userActionsWithoutCurrentAttribute).every(
                (val) => priceGroup.indexOf(val) >= 0 || Number(val) === 0,
              )
            ) {
              // if that is the case then we have a price for this attribute
              // with all the things the user has done so far
              currentAttributeIsActive = true;
              currentAttribute.activeValues += 1;
              break;
            }
          }

          currentAttributeValue.active = currentAttributeIsActive;

          if (
            Object.keys(currentAttribute.values).length > 1 &&
            !currentAttribute.showDropdown
          ) {
            currentAttribute.showDropdown = true;
          }
        }

        currentOptionRegions[region]!.customizations =
          currentRegionCustomizations;

        Object.values(currentRegionCustomizations).forEach(
          (regionCustomization) => {
            if (
              !Object.values(regionCustomization?.values ?? []).some(
                (value) => value.active,
              )
            ) {
              currentOptionRegions[region]!.active = false;
              // eslint-disable-next-line
            return;
            }
          },
        );

        currentOptionRegions[region]!.selectedOptions =
          regions[region]?.selectedOptions || {};
      }

      currentNavigationKey.regions = currentOptionRegions;
      navigationKeyData.push(currentNavigationKey);
    }

    // pricing key is hardcoded and doesn't come from DB, we could add it later to CF service method.
    const currentNavigationKey: NavigationKeyData = {};

    currentNavigationKey.id = 0;
    currentNavigationKey.type = "Pricing";
    currentNavigationKey.optionName = "pricing";
    currentNavigationKey.icon = "icon-shopping-cart";
    navigationKeyData.push(currentNavigationKey);

    return navigationKeyData;
  };

const getNavigationSpecificData: Thunk<RegionResult[]> = (state) => () => {
  const { selectedRegion, regionData } = state;

  const regionDataArray = Object.values(regionData);
  const returnResult: RegionResult[] = [];

  for (const i in regionDataArray) {
    let foundDuplicateNavigationData = false;

    // look if we have duplicate camera position and product rotation data

    for (const result in returnResult) {
      if (
        returnResult[result]?.data?.position?.x ===
          // @ts-ignore
          regionDataArray[i]?.model?.position?.x &&
        returnResult[result]?.data?.position?.y ===
          // @ts-ignore
          regionDataArray[i]?.model?.position?.y &&
        returnResult[result]?.data?.position?.z ===
          // @ts-ignore
          regionDataArray[i]?.model?.position?.z &&
        returnResult[result]?.data?.rotation?.x ===
          // @ts-ignore
          regionDataArray[i]?.model?.rotation?.x &&
        returnResult[result]?.data?.rotation?.y ===
          // @ts-ignore
          regionDataArray[i]?.model?.rotation?.y &&
        returnResult[result]?.data?.rotation?.z ===
          // @ts-ignore
          regionDataArray[i]?.model?.rotation?.z
      ) {
        // we still want the selected region camera button to be guaranteed to show up

        // @ts-ignore
        if (selectedRegion === regionDataArray[i]?.id) {
          if (Number.isNaN(result)) {
            returnResult.splice(Number(result), 1);
          }
        } else {
          // otherwise flag as found duplicate
          foundDuplicateNavigationData = true;
        }
        break;
      }
    }
    // if we dont have a duplicate add the navigation data
    if (!foundDuplicateNavigationData) {
      returnResult.push({
        // @ts-ignore
        region: regionDataArray[i]?.name ?? "",

        // @ts-ignore
        id: regionDataArray[i]?.id ?? 0,

        // @ts-ignore
        data: regionDataArray[i]?.model,
      });
    }
  }
  return returnResult;
};

const getIsPricingAvailable: Thunk<boolean> = (state, dispatch) => () => {
  const userActions = getUserActions(state, dispatch)() || [];
  return !!(
    Object.keys(userActions).length &&
    Object.keys(getCurrentPriceGroups(state, dispatch)() || []).length
  );
};

const getCurrentControls: Thunk = (state) => () => {
  const { selectedRegion, currentNavigationKey } = state;

  const navigationKeyData =
    // @ts-ignore
    getNavigationKeyData(state)()[currentNavigationKey!.currentNavigationKey];

  const currentRegionCustomizationsToDisplay: (
    | RegionCustomization
    | undefined
  )[] = [];

  if (
    typeof navigationKeyData?.regions !== "undefined" &&
    !(typeof navigationKeyData.regions[selectedRegion] === "undefined")
  ) {
    for (const customization in navigationKeyData.regions[selectedRegion]
      ?.customizations) {
      if (
        navigationKeyData.regions![selectedRegion]?.customizations![
          customization
        ]?.showDropdown
      ) {
        currentRegionCustomizationsToDisplay.push(
          navigationKeyData.regions![selectedRegion]?.customizations![
            customization
          ],
        );
      }
    }
  }

  return currentRegionCustomizationsToDisplay;
};

const getUserActions: Thunk<Lookup<UserAction>> = (state) => () =>
  state.userActions ?? {};

const getSelectedRegionData: Thunk = (state) => () => {
  const { selectedRegion, regionData } = state;
  return regionData[selectedRegion];
};

// This is supposed to be a selector but the original code also does a set, so it's a thunk.
const getSelectedOptions: Thunk = (state, dispatch) => () => {
  const { selectedRegion, currentNavigationKey } = state;
  const navigationKeyData =
    // @ts-ignore
    getNavigationKeyData(state)()[currentNavigationKey!.currentNavigationKey];

  // @ts-ignore
  const selectedRegionData = navigationKeyData.regions[selectedRegion];
  const navKeyData = selectedRegionData;

  if (selectedRegion) {
    return selectedRegionData?.selectedOptions;
  }

  if (selectedRegionData?.active) {
    // check selected options if they are still valid

    for (const selectedOption in selectedRegionData.selectedOptions) {
      // @ts-ignore
      const selectedOptionData = navKeyData.selectedOptions[selectedOption];
      // @ts-ignore
      const selectedCustomizations = navKeyData.customizations[selectedOption];

      if (
        selectedOptionData !== 0 &&
        // @ts-ignore
        selectedCustomizations.values?.[selectedOptionData].active
      ) {
        // @ts-ignore
        const payload: SelectedOption = {
          optionId: navigationKeyData!.id?.toString() || "",
          attributeId: selectedOption,
          regionId:
            navigationKeyData!.regions![selectedRegion]!.id.toString() || "",
          attributeValueId: 0,
        };

        dispatch({ type: actions.optionSelected, payload });
        break;
      }
    }
  }

  return null;
};

const getUserActionsShape: Thunk<Lookup<UserAction>> =
  (state, dispatch) => () => {
    const customizationData = state.customerCustomizationData;
    const customizationDataOptions = {};

    const navKeyData = getNavigationKeyData(state, dispatch)()?.filter(
      (nkd: NavigationKeyData) => nkd.id !== 0,
    );

    Object.values(customizationData).forEach((customization) => {
      // @ts-ignore
      Object.values(navKeyData).forEach((navigationKey) => {
        if (navigationKey.id != Number(customization.optionId)) return;

        const currentCustomizationRegion =
          // @ts-ignore
          navigationKey.regions[customization.regionId];

        // @ts-ignore
        Object.entries(currentCustomizationRegion.selectedOptions).forEach(
          ([selectedOption, selectedOptionValueId]) => {
            let customizationDescription = "";
            const regionCustomizations =
              // @ts-ignore
              currentCustomizationRegion.customizations;

            // @ts-ignore
            Object.values(regionCustomizations).forEach(
              (regionCustomization) => {
                // @ts-ignore
                Object.values(regionCustomization.values).forEach(
                  (customizationValue) => {
                    if (customizationValue.id == selectedOptionValueId) {
                      customizationDescription = `${regionCustomization.attributeName}: ${customizationValue.displayValue}`;
                    }
                  },
                );
              },
            );

            if (
              selectedOptionValueId !== 0 &&
              // @ts-ignore
              !customizationDataOptions[selectedOptionValueId]
            ) {
              // @ts-ignore
              customizationDataOptions[selectedOptionValueId] = {
                optionId: Number(customization.optionId),
                optionName: navigationKey.type,
                regionId: Number(customization.regionId),
                // @ts-ignore
                regionName: currentCustomizationRegion.regionName,
                regionIsMutuallyExclusive:
                  // @ts-ignore
                  currentCustomizationRegion.isMutuallyExclusive,
                attributeName:
                  // @ts-ignore
                  currentCustomizationRegion.customizations[selectedOption]
                    .attributeName,
                attributeId:
                  // @ts-ignore
                  currentCustomizationRegion.customizations[selectedOption].id,
                optionToAttributeId:
                  // @ts-ignore
                  currentCustomizationRegion.customizations[selectedOption]
                    .optionToAttributeId,
                attributeValue:
                  // @ts-ignore
                  currentCustomizationRegion.customizations[selectedOption]
                    .values[selectedOptionValueId],
                customizationDescription,
                additionalText: customization.values || [],
              };
            }
          },
        );
      });
    });

    return { ...customizationDataOptions };
  };

const getCustomizationDescriptions: Thunk = (state) => () => {
  const { userActions } = state;

  const descriptions: Lookup<any> = {};
  Object.values(userActions || []).forEach((action) => {
    const combinationId = `${action.regionId}${action.optionId}`;
    if (!descriptions[combinationId]) {
      descriptions[combinationId] = {
        locationName: action.regionName,
        type: action.optionName,
        // @ts-ignore
        additionalText: action.additionalText,
        details: [],
      };
    }
    descriptions[combinationId].details.push(action.customizationDescription);
  });

  return descriptions;
};

const getCurrentRegionSelectedSpecialOptions: Thunk<SpecialOptions> =
  (state, dispatch) => () => {
    const { currentNavigationKey, selectedRegion } = state;
    const specialOptions: SpecialOptions = {
      numberOfLines: 1,
      maxCharacters: 20,
      fontColor: "black",
      numericOnly: false,
    };

    if (!currentNavigationKey) {
      return specialOptions;
    }

    const navigationKeyData = getNavigationKeyData(state, dispatch)()![
      currentNavigationKey!.currentNavigationKey!
    ]!;

    if (!navigationKeyData) {
      return specialOptions;
    }

    const { selectedOptions } = navigationKeyData.regions![selectedRegion]!;

    Object.keys(selectedOptions!).forEach((optionId) => {
      if (selectedOptions![optionId] !== 0) {
        const { attributeName } =
          navigationKeyData!.regions![selectedRegion]!.customizations![
            optionId
          ]!;
        switch (attributeName) {
          case "number of lines":
            specialOptions.numberOfLines =
              navigationKeyData!.regions![selectedRegion]!.customizations![
                optionId
              ]!.values![selectedOptions![optionId]!]!.numericValue!;
            break;
          case "max characters":
            specialOptions.maxCharacters =
              navigationKeyData!.regions![selectedRegion]!.customizations![
                optionId
              ]!.values![selectedOptions![optionId!]!]!.numericValue;
            break;
          case "font color":
            specialOptions.fontColor =
              navigationKeyData!.regions![selectedRegion!]!.customizations![
                optionId!
              ]!.values![selectedOptions![optionId!]!]!.stringValue;
            break;
          case "numeric only":
            specialOptions.numericOnly = true;
            break;
          default:
        }
      }
    });

    return specialOptions;
  };

const getSelectedNavigationMenuOption: Thunk = (state) => () => {
  const {
    navigationViewModelData: { customizableOptions },
    // @ts-ignore
    currentNavigationKey: { currentNavigationKey },
  } = state;
  return Object.values(customizableOptions)[currentNavigationKey];
};

const getCurrentPriceGroups: Thunk<Lookup<PriceGroup>> = (state) => () => {
  const { userActions } = state;
  const { priceGroups } = state.navigationViewModelData;

  const currentPriceGroups: Lookup<PriceGroup> = {};
  const userActionsKeys = Object.keys(userActions || {});

  // populate the priceGroups structure to return with price groups containing all selected user actions
  if (userActionsKeys.length) {
    Object.values(priceGroups || {}).forEach((priceGroup) => {
      const pricedAttributeKeys = Object.keys(priceGroup.attributesValues);
      if (
        userActionsKeys.every(
          (keyValue) =>
            pricedAttributeKeys.indexOf(keyValue) >= 0 &&
            pricedAttributeKeys.every(
              (keyValue) => userActionsKeys.indexOf(keyValue) >= 0,
            ),
        )
      ) {
        //  Combine groups with matching prices
        let foundMatchingPrice = false;

        Object.values(currentPriceGroups).forEach((currentPriceGroup) => {
          if (priceGroup.price === currentPriceGroup.price) {
            foundMatchingPrice = true;
            currentPriceGroup.startQuantity = Math.min(
              priceGroups?.[priceGroup.priceGroupGuid]?.startQuantity || 0,
              currentPriceGroup.startQuantity,
            );
            currentPriceGroup.endQuantity = Math.max(
              priceGroups?.[priceGroup.priceGroupGuid]?.endQuantity || 0,
              currentPriceGroup.endQuantity,
            );

            return;
          }
        });

        if (!foundMatchingPrice) {
          // @ts-ignore
          currentPriceGroups[priceGroup.priceGroupGuid] =
            priceGroups?.[priceGroup.priceGroupGuid];
        }
      }
    });
  }

  return currentPriceGroups;
};

const getCurrentPriceGroupSortedKeys: Thunk = (state, dispatch) => () => {
  const priceGroups = getCurrentPriceGroups(state, dispatch)();
  return Object.keys(priceGroups || []).sort(
    // @ts-ignore
    (a, b) => priceGroups[a].startQuantity - priceGroups[b].startQuantity,
  );
};

const getCurrentPriceGroupMinimumBuy: Thunk = (state, dispatch) => () => {
  const { productData } = state;
  const currentPriceGroupSortedKeys = getCurrentPriceGroupSortedKeys(
    state,
    dispatch,
  )();
  const currentPriceGroups = getCurrentPriceGroups(state, dispatch)();

  return currentPriceGroupSortedKeys.length &&
    // @ts-ignore
    currentPriceGroups[currentPriceGroupSortedKeys[0]].startQuantity >
      productData.minimumBuy
    ? // @ts-ignore
      currentPriceGroups[currentPriceGroupSortedKeys[0]].startQuantity
    : productData.minimumBuy;
};

const getSpecialQuantityValues: Thunk = (state, dispatch) => () => {
  const { productData } = state;

  let startBuy = 1;
  let mustBuySteps = 1;
  const values = [];

  const currentPriceGroupMinimumBuy = getCurrentPriceGroupMinimumBuy(
    state,
    dispatch,
  )();

  if (currentPriceGroupMinimumBuy > 1) {
    startBuy = currentPriceGroupMinimumBuy;
  } else if (productData.mustBuy > 1) {
    startBuy = productData.mustBuy;
  }

  if (productData.mustBuy > 1) {
    mustBuySteps = productData.mustBuy;
  }

  if (startBuy > 1 || mustBuySteps > 1) {
    let currentQuantity = startBuy;

    for (let i = 1; i <= 15; i += 1) {
      values.push(currentQuantity);
      currentQuantity += mustBuySteps;
    }

    values.push("Other");
  }

  return values;
};

const getCurrentPriceGroupMaxQuantity: Thunk<number> =
  (state, dispatch) => () => {
    const currentPriceGroups = getCurrentPriceGroups(state, dispatch)();
    const currentPriceGroupSortedKeys =
      getCurrentPriceGroupSortedKeys(state, dispatch)() || [];

    return currentPriceGroupSortedKeys.length
      ? // @ts-ignore
        currentPriceGroups[
          currentPriceGroupSortedKeys[currentPriceGroupSortedKeys.length - 1]
        ].endQuantity
      : 9999;
  };

const getMinMustBuyText: Thunk<string> = (state, dispatch) => () => {
  const { productData } = state;

  const currentPriceGroupMinimumBuy = getCurrentPriceGroupMinimumBuy(
    state,
    dispatch,
  )();

  if (productData.mustBuy > 1) {
    return currentPriceGroupMinimumBuy === 1
      ? `You must buy a minimum of ${productData.mustBuy} at a time due to manufacturer packaging.`
      : `You must buy a minimum of ${currentPriceGroupMinimumBuy} and in additional increments of ${productData.mustBuy} due to manufacturer packaging.`;
  }

  return currentPriceGroupMinimumBuy > 1
    ? `You must buy a minimum of ${currentPriceGroupMinimumBuy} due to manufacturer packaging.`
    : "";
};

const getPriceGroupGUIDForProductQuantity: Thunk<string> =
  (state, dispatch) => (quantity) => {
    const priceGroups = getCurrentPriceGroups(state, dispatch)() || {};

    let guid = "";

    Object.values(priceGroups).forEach((priceGroup) => {
      if (
        quantity >= priceGroup.startQuantity &&
        quantity <= priceGroup.endQuantity
      ) {
        guid = priceGroup.priceGroupGuid;

        return;
      }
    });

    return guid;
  };

const getCustomizationDataToSave: Thunk =
  (state, dispatch) => (quantity: number) => {
    const {
      itemNumberId,
      productData: { productToModelId },
      customerCustomizationData,
      fabric: { regions },
    } = state;

    const customizationData = [];
    // @ts-ignore
    const customizationObjects = [];

    // pick only the state data customerCustomizationData we care about
    Object.values(customerCustomizationData).forEach((customization) => {
      const { assetId, clientIdentifier, regionId } = customization;
      const regionJson = regions[regionId];

      // @ts-ignore
      regionJson.objects.forEach((o) =>
        customizationObjects.push({
          assetId,
          clientIdentifier,
          regionId,
          objectJSON: o,
        }),
      );
    });

    // create our customizationData array to post to endpoint
    customizationData.push({
      itemNumberId,
      quantity,
      productToModelId,
      priceGroupGUID: getPriceGroupGUIDForProductQuantity(
        state,
        dispatch,
      )(quantity),
      // @ts-ignore
      stateData: customizationObjects,
    });

    return customizationData;
  };

export default {
  getNavigationKeyData,
  getNavigationSpecificData,
  getIsPricingAvailable,
  getUserActions,
  getCurrentControls,
  getSelectedRegionData,
  getSelectedOptions,
  getUserActionsShape,
  getCustomizationDescriptions,
  getSelectedNavigationMenuOption,
  getCurrentRegionSelectedSpecialOptions,
  getCurrentPriceGroups,
  getCurrentPriceGroupSortedKeys,
  getSpecialQuantityValues,
  getCurrentPriceGroupMaxQuantity,
  getCurrentPriceGroupMinimumBuy,
  getMinMustBuyText,
  getCustomizationDataToSave,
};
